import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ALL_CHANNEL, playChatSound, setSoundEnabled, soundEnabled, wsUrl } from "@/lib/chat";

const ChatContext = createContext(null);
const PAGE = 50;

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [canMarkImportant, setCanMarkImportant] = useState(false);
  const [totals, setTotals] = useState({ count: 0, important: 0 });
  const [messages, setMessages] = useState({}); // key -> {items, has_more, loaded}
  const [activeKey, setActiveKey] = useState(ALL_CHANNEL);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(soundEnabled());
  const [connected, setConnected] = useState(false);
  const viewers = useRef(0); // componenti che stanno mostrando il canale attivo
  const activeRef = useRef(ALL_CHANNEL);
  const wsRef = useRef(null);
  const retry = useRef(0);
  const seen = useRef(new Set()); // dedupe eventi WS (fan-out multi-processo)
  activeRef.current = activeKey;

  const applyUnread = useCallback((payload) => {
    setTotals({ count: payload.count || 0, important: payload.important || 0 });
    if (payload.channels) {
      setChannels((cs) => cs.map((c) => ({ ...c, ...(payload.channels[c.key] || {}) })));
    }
  }, []);

  const refreshChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/channels");
      setChannels(data.items);
      setCanMarkImportant(!!data.can_mark_important);
      setTotals({ count: data.total_unread, important: data.total_important });
    } catch (e) { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async (key, before = null) => {
    const { data } = await api.get("/chat/messages", { params: { channel: key, limit: PAGE, before } });
    setMessages((m) => {
      const prev = m[key]?.items || [];
      const ids = new Set(data.items.map((x) => x.id));
      const merged = before ? [...data.items, ...prev.filter((x) => !ids.has(x.id))] : [...data.items, ...prev.filter((x) => !ids.has(x.id))];
      merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      return { ...m, [key]: { items: merged, has_more: before ? data.has_more : (m[key]?.has_more ?? data.has_more), loaded: true } };
    });
    return data;
  }, []);

  const markRead = useCallback(async (key, ids) => {
    if (!ids.length) return;
    try {
      const { data } = await api.post("/chat/read", { channel: key, message_ids: ids });
      applyUnread(data);
      ids.forEach((id) => toast.dismiss(`chat-${id}`));
      setMessages((m) => m[key] ? { ...m, [key]: { ...m[key], items: m[key].items.map((x) => ids.includes(x.id) ? { ...x, read: true } : x) } } : m);
    } catch (e) { /* ignore */ }
  }, [applyUnread]);

  const isViewing = useCallback((key) => viewers.current > 0 && activeRef.current === key && document.visibilityState === "visible", []);

  const openChat = useCallback((key) => {
    if (key) setActiveKey(key);
    setDrawerOpen(true);
  }, []);

  const notify = useCallback((msg) => {
    const title = `Nuovo messaggio da ${msg.user_name} · ${msg.user_label}`;
    const preview = msg.message.length > 90 ? `${msg.message.slice(0, 90)}…` : msg.message;
    const action = { label: "Apri", onClick: () => openChat(msg.channel) };
    if (msg.is_important) {
      toast.error(`📌 IMPORTANTE — ${title}`, { id: `chat-${msg.id}`, description: preview, duration: Infinity, action, closeButton: true });
    } else {
      toast(title, { id: `chat-${msg.id}`, description: preview, duration: 6000, action });
    }
    if (soundEnabled()) playChatSound(msg.is_important);
  }, [openChat]);

  const onIncoming = useCallback((msg) => {
    if (seen.current.has(msg.id)) return;
    seen.current.add(msg.id);
    if (seen.current.size > 2000) seen.current = new Set([...seen.current].slice(-1000));
    setMessages((m) => {
      const cur = m[msg.channel];
      if (!cur?.loaded || cur.items.some((x) => x.id === msg.id)) return m;
      return { ...m, [msg.channel]: { ...cur, items: [...cur.items, msg] } };
    });
    if (msg.user_id === user?.id || msg.notify === false) return;
    if (isViewing(msg.channel)) { markRead(msg.channel, [msg.id]); return; }
    setTotals((t) => ({ count: t.count + 1, important: t.important + (msg.is_important ? 1 : 0) }));
    setChannels((cs) => cs.map((c) => c.key === msg.channel
      ? { ...c, unread: (c.unread || 0) + 1, important: (c.important || 0) + (msg.is_important ? 1 : 0) } : c));
    notify(msg);
  }, [user?.id, isViewing, markRead, notify]);

  // --- WebSocket con riconnessione (nessun polling periodico)
  useEffect(() => {
    if (!user) return undefined;
    let closed = false;
    let ping = null;
    let timer = null;
    const connect = () => {
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onopen = () => { retry.current = 0; setConnected(true); refreshChannels(); ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000); };
      ws.onmessage = (ev) => {
        if (ev.data === "pong") return;
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message") onIncoming(data.message);
          else if (data.type === "unread") applyUnread(data);
        } catch (e) { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false); clearInterval(ping);
        if (closed) return;
        const delay = Math.min(30000, 1000 * 2 ** retry.current++);
        timer = setTimeout(async () => { try { await api.get("/auth/me"); } catch (e) { /* refresh cookie */ } connect(); }, delay);
      };
      ws.onerror = () => ws.close();
    };
    refreshChannels();
    connect();
    return () => {
      closed = true; clearInterval(ping); clearTimeout(timer);
      const ws = wsRef.current;
      if (ws) { if (ws.readyState === WebSocket.OPEN) ws.close(); else ws.onopen = () => ws.close(); }
    };
  }, [user, refreshChannels, onIncoming, applyUnread]);

  const toggleSound = useCallback(() => { setSoundOn((v) => { setSoundEnabled(!v); return !v; }); }, []);
  const registerViewer = useCallback(() => { viewers.current += 1; return () => { viewers.current -= 1; }; }, []);

  const send = useCallback(async (key, text, important) => {
    const { data } = await api.post("/chat/messages", { channel: key, message: text, is_important: !!important });
    setMessages((m) => {
      const cur = m[key];
      if (!cur?.loaded || cur.items.some((x) => x.id === data.id)) return m;
      return { ...m, [key]: { ...cur, items: [...cur.items, data] } };
    });
    return data;
  }, []);

  const value = useMemo(() => ({
    channels, totals, messages, activeKey, setActiveKey, drawerOpen, setDrawerOpen, openChat, soundOn, toggleSound,
    canMarkImportant, connected, loadMessages, markRead, send, registerViewer, refreshChannels,
  }), [channels, totals, messages, activeKey, drawerOpen, openChat, soundOn, toggleSound, canMarkImportant, connected,
    loadMessages, markRead, send, registerViewer, refreshChannels]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  return useContext(ChatContext);
}
